import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const TripGpsForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, put, loading } = useApi();
  
  const [formData, setFormData] = useState({
    tripId: '',
    eventId: '',
    videoId: '',
    timestamp: '',
    latitude: '',
    longitude: '',
    altitude: '',
    speed: '',
    heading: ''
  });

  const [trips, setTrips] = useState([]);
  const [events, setEvents] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAllData();
  }, [id]);

  const fetchAllData = async () => {
    setLoadingData(true);
    
    // Fetch trips
    const tripsRes = await get('/api/trips/filter?size=100');
    if (tripsRes.data) setTrips(tripsRes.data.content || []);

    // Fetch events
    const eventsRes = await get('/api/events/filter?size=100');
    if (eventsRes.data) setEvents(eventsRes.data.content || []);

    // Fetch videos
    const videosRes = await get('/api/videos/filter?size=100');
    if (videosRes.data) setVideos(videosRes.data.content || []);

    // Fetch record data if editing
    if (id) {
      const recordRes = await get(`/api/trip-gps/${id}`);
      if (recordRes.data) {
        setFormData({
          tripId: recordRes.data.tripId || '',
          eventId: recordRes.data.eventId || '',
          videoId: recordRes.data.videoId || '',
          timestamp: recordRes.data.timestamp ? recordRes.data.timestamp.slice(0, 16) : '',
          latitude: recordRes.data.latitude || '',
          longitude: recordRes.data.longitude || '',
          altitude: recordRes.data.altitude || '',
          speed: recordRes.data.speed || '',
          heading: recordRes.data.heading || ''
        });
      }
    }

    setLoadingData(false);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.tripId) {
      newErrors.tripId = 'Trip is required';
    }
    if (!formData.timestamp) {
      newErrors.timestamp = 'Timestamp is required';
    }
    if (formData.latitude && (formData.latitude < -90 || formData.latitude > 90)) {
      newErrors.latitude = 'Latitude must be between -90 and 90';
    }
    if (formData.longitude && (formData.longitude < -180 || formData.longitude > 180)) {
      newErrors.longitude = 'Longitude must be between -180 and 180';
    }
    if (formData.speed && formData.speed < 0) {
      newErrors.speed = 'Speed cannot be negative';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Convert numeric fields
    const submitData = {
      tripId: parseInt(formData.tripId),
      eventId: formData.eventId ? parseInt(formData.eventId) : null,
      videoId: formData.videoId ? parseInt(formData.videoId) : null,
      timestamp: formData.timestamp,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      altitude: formData.altitude ? parseFloat(formData.altitude) : null,
      speed: formData.speed ? parseFloat(formData.speed) : null,
      heading: formData.heading ? parseFloat(formData.heading) : null
    };

    let response;
    if (id) {
      response = await put(`/api/trip-gps/${id}`, submitData);
    } else {
      response = await post('/api/trip-gps', submitData);
    }

    if (!response.error) {
      navigate('/trip-gps/filter');
    }
  };

  if (loadingData || (loading && id)) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/trip-gps/filter')}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {id ? 'Edit GPS Record' : 'Add New GPS Record'}
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Trip Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trip <span className="text-red-500">*</span>
              </label>
              <select
                name="tripId"
                value={formData.tripId}
                onChange={handleChange}
                className={`input-field ${errors.tripId ? 'border-red-500' : ''}`}
              >
                <option value="">Select a trip</option>
                {trips.map(trip => (
                  <option key={trip.id} value={trip.id}>
                    Trip #{trip.id} - {trip.label || 'No label'} 
                    {trip.driver && ` (${trip.driver.fullName})`}
                  </option>
                ))}
              </select>
              {errors.tripId && (
                <p className="mt-1 text-sm text-red-600">{errors.tripId}</p>
              )}
            </div>

            {/* Event Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event (Optional)
              </label>
              <select
                name="eventId"
                value={formData.eventId}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">No event</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    Event #{event.id} - {event.eventType?.description || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>

            {/* Video Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video (Optional)
              </label>
              <select
                name="videoId"
                value={formData.videoId}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">No video</option>
                {videos.map(video => (
                  <option key={video.id} value={video.id}>
                    Video #{video.id}
                  </option>
                ))}
              </select>
            </div>

            {/* Timestamp */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timestamp <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                name="timestamp"
                value={formData.timestamp}
                onChange={handleChange}
                className={`input-field ${errors.timestamp ? 'border-red-500' : ''}`}
              />
              {errors.timestamp && (
                <p className="mt-1 text-sm text-red-600">{errors.timestamp}</p>
              )}
            </div>

            {/* Latitude */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Latitude
              </label>
              <input
                type="number"
                step="any"
                name="latitude"
                value={formData.latitude}
                onChange={handleChange}
                className={`input-field ${errors.latitude ? 'border-red-500' : ''}`}
                placeholder="-90 to 90"
              />
              {errors.latitude && (
                <p className="mt-1 text-sm text-red-600">{errors.latitude}</p>
              )}
            </div>

            {/* Longitude */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Longitude
              </label>
              <input
                type="number"
                step="any"
                name="longitude"
                value={formData.longitude}
                onChange={handleChange}
                className={`input-field ${errors.longitude ? 'border-red-500' : ''}`}
                placeholder="-180 to 180"
              />
              {errors.longitude && (
                <p className="mt-1 text-sm text-red-600">{errors.longitude}</p>
              )}
            </div>

            {/* Altitude */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Altitude (meters)
              </label>
              <input
                type="number"
                step="any"
                name="altitude"
                value={formData.altitude}
                onChange={handleChange}
                className="input-field"
                placeholder="e.g., 100.5"
              />
            </div>

            {/* Speed */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Speed (km/h)
              </label>
              <input
                type="number"
                step="any"
                name="speed"
                value={formData.speed}
                onChange={handleChange}
                className={`input-field ${errors.speed ? 'border-red-500' : ''}`}
                placeholder="e.g., 60.5"
              />
              {errors.speed && (
                <p className="mt-1 text-sm text-red-600">{errors.speed}</p>
              )}
            </div>

            {/* Heading */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Heading (degrees)
              </label>
              <input
                type="number"
                step="any"
                name="heading"
                value={formData.heading}
                onChange={handleChange}
                className="input-field"
                placeholder="0-360"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/trip-gps/filter')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : id ? 'Update Record' : 'Create Record'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TripGpsForm;